import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Card,
  Col,
  Container,
  Form,
  Row,
  Table,
} from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";

import {
  listNewsletterSubscribersAdmin,
  exportNewsletterSubscribersAdmin,
  sendNewsletterAdmin,
} from "../../actions/newsletterActions";
import { NEWSLETTER_SEND_RESET } from "../../constants/newsletterConstants";
import Loader from "../../components/Loader";
import Message from "../../components/Message";

function formatDate(value) {
  if (!value) return "-";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "-" : date.toLocaleString();
}

function csvEscape(value) {
  return `"${String(value ?? "").replace(/"/g, '""')}"`;
}

function buildSubscriberCsv(subscribers = []) {
  const headers = ["email", "subscribed", "subscribedAt", "unsubscribeUrl"];
  const rows = subscribers.map((subscriber) =>
    [
      subscriber.email,
      subscriber.subscribed ? "true" : "false",
      subscriber.subscribedAt || "",
      subscriber.unsubscribeUrl || "",
    ]
      .map(csvEscape)
      .join(","),
  );

  return [headers.join(","), ...rows].join("\n");
}

function downloadCsv(filename, csv) {
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");

  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
}

export default function AdminNewsletterSubscribersScreen() {
  const dispatch = useDispatch();
  const [status, setStatus] = useState("active");
  const [page, setPage] = useState(1);
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");

  const [exportState, setExportState] = useState({
    loading: false,
    error: "",
    message: "",
  });

  const listState = useSelector(
    (state) => state.newsletterAdminList || {},
  );
  const sendState = useSelector((state) => state.newsletterSend || {});

  const {
    loading = false,
    error = "",
    subscribers = [],
    counts = { total: 0, active: 0, unsubscribed: 0 },
    pages = 1,
    filteredTotal = 0,
  } = listState;

  const params = useMemo(() => ({ status, page, limit: 50 }), [status, page]);

  useEffect(() => {
    dispatch(listNewsletterSubscribersAdmin(params));
  }, [dispatch, params]);

  const changeStatus = (event) => {
    setStatus(event.target.value);
    setPage(1);
  };

  const clearSendResult = () => {
    if (sendState.success || sendState.error) {
      dispatch({ type: NEWSLETTER_SEND_RESET });
    }
  };

  const submitMarketingEmail = async (event) => {
    event.preventDefault();

    if (!subject.trim() || !message.trim()) return;
    if (
      !window.confirm(
        `Send this marketing email to ${counts.active} active subscriber${
          counts.active === 1 ? "" : "s"
        }?`,
      )
    ) {
      return;
    }

    try {
      await dispatch(sendNewsletterAdmin({ subject, message }));
      setSubject("");
      setMessage("");
    } catch {
      // The reducer presents the safe API error.
    }
  };

  const loadActiveExport = async () => {
    setExportState({ loading: true, error: "", message: "" });
    try {
      const data = await exportNewsletterSubscribersAdmin();
      return Array.isArray(data?.subscribers) ? data.subscribers : [];
    } catch (exportError) {
      setExportState({
        loading: false,
        error:
          exportError?.message ||
          "We could not export active newsletter subscribers right now.",
        message: "",
      });
      return null;
    }
  };

  const downloadActiveCsv = async () => {
    const activeSubscribers = await loadActiveExport();
    if (!activeSubscribers) return;

    downloadCsv(
      "active-newsletter-subscribers.csv",
      buildSubscriberCsv(activeSubscribers),
    );
    setExportState({
      loading: false,
      error: "",
      message: `Downloaded ${activeSubscribers.length} active subscriber${
        activeSubscribers.length === 1 ? "" : "s"
      }.`,
    });
  };

  const copyActiveCsv = async () => {
    const activeSubscribers = await loadActiveExport();
    if (!activeSubscribers) return;

    try {
      await navigator.clipboard.writeText(buildSubscriberCsv(activeSubscribers));
      setExportState({
        loading: false,
        error: "",
        message: `Copied ${activeSubscribers.length} active subscriber row${
          activeSubscribers.length === 1 ? "" : "s"
        }.`,
      });
    } catch {
      setExportState({
        loading: false,
        error: "Your browser could not copy the subscriber export.",
        message: "",
      });
    }
  };

  return (
    <Container className="py-4">
      <Row className="align-items-end g-3 mb-4">
        <Col>
          <h1>Newsletter Subscribers</h1>
          <div className="text-muted">
            Manage subscribers, send updates and export active recipients.
          </div>
        </Col>

        <Col md="auto">
          <Form.Label htmlFor="newsletter-status">Status</Form.Label>
          <Form.Select
            id="newsletter-status"
            value={status}
            onChange={changeStatus}
          >
            <option value="active">Active</option>
            <option value="unsubscribed">Unsubscribed</option>
            <option value="all">All</option>
          </Form.Select>
        </Col>
      </Row>

      <Row className="g-3 mb-4">
        <Col md={6}>
          <Card className="h-100">
            <Card.Body>
              <div className="text-muted text-uppercase fw-bold">Total</div>
              <div className="fs-2 fw-bold">{counts.total}</div>
            </Card.Body>
          </Card>
        </Col>
        <Col md={6}>
          <Card className="h-100">
            <Card.Body>
              <div className="text-muted text-uppercase fw-bold">Active</div>
              <div className="fs-2 fw-bold">{counts.active}</div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Card className="mb-4">
        <Card.Body>
          <Card.Title>Send marketing email</Card.Title>
          <Form onSubmit={submitMarketingEmail}>
            <Form.Group className="mb-3" controlId="newsletter-subject">
              <Form.Label>Subject</Form.Label>
              <Form.Control
                value={subject}
                maxLength={150}
                required
                onChange={(event) => {
                  clearSendResult();
                  setSubject(event.target.value);
                }}
              />
            </Form.Group>

            <Form.Group className="mb-3" controlId="newsletter-message">
              <Form.Label>Message</Form.Label>
              <Form.Control
                as="textarea"
                rows={6}
                value={message}
                maxLength={5000}
                required
                onChange={(event) => {
                  clearSendResult();
                  setMessage(event.target.value);
                }}
              />
            </Form.Group>

            {sendState.error ? (
              <Alert variant="danger">{sendState.error}</Alert>
            ) : null}
            {sendState.success ? (
              <Alert variant={sendState.result?.failed ? "warning" : "success"}>
                {sendState.result?.message}
              </Alert>
            ) : null}

            <Button
              type="submit"
              variant="dark"
              disabled={
                sendState.loading ||
                counts.active < 1 ||
                !subject.trim() ||
                !message.trim()
              }
            >
              {sendState.loading
                ? "Sending..."
                : `Send to ${counts.active} active`}
            </Button>
          </Form>
        </Card.Body>
      </Card>

      <Card className="mb-4">
        <Card.Body>
          <Card.Title>Manual newsletter export</Card.Title>
          <p className="text-muted">
            Export active subscribers only. The CSV includes each email address
            and its unsubscribe link.
          </p>

          {exportState.error ? (
            <Alert variant="danger">{exportState.error}</Alert>
          ) : null}
          {exportState.message ? (
            <Alert variant="success">{exportState.message}</Alert>
          ) : null}

          <div className="d-flex flex-wrap gap-2">
            <Button
              type="button"
              variant="dark"
              disabled={exportState.loading || counts.active < 1}
              onClick={downloadActiveCsv}
            >
              {exportState.loading ? "Preparing..." : "Download active CSV"}
            </Button>
            <Button
              type="button"
              variant="outline-dark"
              disabled={exportState.loading || counts.active < 1}
              onClick={copyActiveCsv}
            >
              Copy active CSV
            </Button>
          </div>
        </Card.Body>
      </Card>

      {error ? <Message variant="danger">{error}</Message> : null}
      {loading ? <Loader /> : null}

      <Table striped bordered hover responsive>
        <thead>
          <tr>
            <th>Email</th>
            <th>Status</th>
            <th>Subscribed</th>
            <th>Unsubscribed</th>
            <th>Source</th>
          </tr>
        </thead>
        <tbody>
          {subscribers.map((subscriber) => (
            <tr key={subscriber.id || subscriber._id}>
              <td>{subscriber.email}</td>
              <td>
                <Badge
                  bg={subscriber.status === "active" ? "success" : "secondary"}
                >
                  {subscriber.status}
                </Badge>
              </td>
              <td>{formatDate(subscriber.subscribedAt)}</td>
              <td>{formatDate(subscriber.unsubscribedAt)}</td>
              <td>{subscriber.source || "-"}</td>
            </tr>
          ))}

          {!subscribers.length && !loading ? (
            <tr>
              <td colSpan={5} className="text-center text-muted">
                No subscribers found
              </td>
            </tr>
          ) : null}
        </tbody>
      </Table>

      <div className="d-flex justify-content-between align-items-center gap-3">
        <div className="text-muted small">
          {filteredTotal} {status === "all" ? "total" : status} subscriber
          {filteredTotal === 1 ? "" : "s"}
        </div>
        <div className="d-flex gap-2">
          <Button
            variant="outline-dark"
            size="sm"
            disabled={page <= 1 || loading}
            onClick={() => setPage((current) => Math.max(1, current - 1))}
          >
            Previous
          </Button>
          <span className="align-self-center small">
            Page {page} of {pages}
          </span>
          <Button
            variant="outline-dark"
            size="sm"
            disabled={page >= pages || loading}
            onClick={() => setPage((current) => current + 1)}
          >
            Next
          </Button>
        </div>
      </div>
    </Container>
  );
}
